import React, { useEffect, useState, useRef } from "react";
import { WifiOff, Wifi } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import pt from "@/lib/i18n";

/**
 * Global offline indicator. Slides down from the top when the browser loses
 * connectivity; briefly flashes a copper "Ligação restaurada ✓" on reconnect.
 */
const OfflineBanner = () => {
    const [online, setOnline] = useState(() =>
        typeof navigator === "undefined" ? true : navigator.onLine,
    );
    const [showRestored, setShowRestored] = useState(false);
    const wasOffline = useRef(false);

    useEffect(() => {
        const handleOnline = () => {
            setOnline(true);
            if (wasOffline.current) {
                setShowRestored(true);
                setTimeout(() => setShowRestored(false), 2500);
            }
            wasOffline.current = false;
        };
        const handleOffline = () => {
            setOnline(false);
            wasOffline.current = true;
        };
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    const offlineLabel = pt.offline?.banner || "Sem ligação — modo offline activo";
    const restoredLabel = pt.offline?.restored || "Ligação restaurada";

    return (
        <AnimatePresence>
            {!online && (
                <motion.div
                    key="offline"
                    initial={{ y: -40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -40, opacity: 0 }}
                    className="fixed top-0 left-0 right-0 z-[60] py-2 px-4 flex items-center justify-center gap-2"
                    style={{
                        background: "linear-gradient(90deg, rgba(245,124,0,0.95), rgba(245,158,11,0.95))",
                        color: "white",
                        boxShadow: "0 4px 14px rgba(245,124,0,0.30)",
                    }}
                    data-testid="offline-banner"
                >
                    <WifiOff size={14} />
                    <span className="font-body text-sm font-medium">{offlineLabel}</span>
                </motion.div>
            )}
            {online && showRestored && (
                <motion.div
                    key="restored"
                    initial={{ y: -40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -40, opacity: 0 }}
                    className="fixed top-0 left-0 right-0 z-[60] py-2 px-4 flex items-center justify-center gap-2"
                    style={{
                        background: "linear-gradient(90deg, rgba(46,125,50,0.95), rgba(56,142,60,0.95))",
                        color: "white",
                        boxShadow: "0 4px 14px rgba(46,125,50,0.30)",
                    }}
                    data-testid="online-restored-banner"
                >
                    <Wifi size={14} />
                    <span className="font-body text-sm font-medium">{restoredLabel} ✓</span>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default OfflineBanner;
